import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Loader2, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { useContent } from "../context/ContentContext";

const clone = (o) => JSON.parse(JSON.stringify(o));

function Field({ label, value, onChange, textarea, rows = 3, hint }) {
  return (
    <label className="block">
      <span className="block text-sm font-600 text-charcoal mb-1">{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-emerald resize-y bg-white"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-emerald bg-white"
        />
      )}
      {hint && <span className="block text-xs text-olive/60 mt-1">{hint}</span>}
    </label>
  );
}

function Section({ title, desc, children }) {
  return (
    <div className="bg-white border border-border rounded-sm p-6 md:p-8">
      <h3 className="font-display text-xl font-600 text-charcoal">{title}</h3>
      {desc && <p className="text-olive text-sm mt-1">{desc}</p>}
      <div className="space-y-5 mt-5">{children}</div>
    </div>
  );
}

export default function SiteContentEditor() {
  const { content, refresh } = useContent();
  const [data, setData] = useState(() => clone(content));
  const [saving, setSaving] = useState(false);

  // Re-sync local form when the global content changes (e.g. after a save).
  useEffect(() => {
    setData(clone(content));
  }, [content]);

  const edit = (mutator) =>
    setData((prev) => {
      const next = clone(prev);
      mutator(next);
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", {
        hero: data.hero,
        contact: data.contact,
        services: data.services,
        founders: data.founders,
      });
      await refresh();
      toast.success("Website updated — your changes are live!");
    } catch (e) {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="site-content-editor" className="space-y-6 pb-24">
      <div className="bg-emerald/5 border border-emerald/20 rounded-sm p-4 text-sm text-charcoal/80">
        Edit your website text below and click <strong>Save Changes</strong> — updates go live instantly.
        Photos, logo, and page layout stay exactly the same. To add/remove whole services or change images, just message me.
      </div>

      <Section title="Homepage Hero" desc="The large headline area at the very top of your homepage.">
        <Field label="Small label (eyebrow)" value={data.hero.eyebrow} onChange={(v) => edit((d) => { d.hero.eyebrow = v; })} />
        <Field label="Headline — line 1" value={data.hero.titleLine1} onChange={(v) => edit((d) => { d.hero.titleLine1 = v; })} />
        <Field label="Headline — highlighted line (gold)" value={data.hero.titleHighlight} onChange={(v) => edit((d) => { d.hero.titleHighlight = v; })} />
        <Field label="Intro paragraph" textarea value={data.hero.subtext} onChange={(v) => edit((d) => { d.hero.subtext = v; })} />
      </Section>

      <Section title="Contact Details" desc="Shown in the footer & Contact page, and used for your Google business listing.">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Phone" value={data.contact.phone} onChange={(v) => edit((d) => { d.contact.phone = v; })} />
          <Field label="Email" value={data.contact.email} onChange={(v) => edit((d) => { d.contact.email = v; })} />
          <Field label="Hours" value={data.contact.hours} onChange={(v) => edit((d) => { d.contact.hours = v; })} />
          <Field label="Service area line" value={data.contact.areaLine} onChange={(v) => edit((d) => { d.contact.areaLine = v; })} />
        </div>
        <Field
          label="Service areas"
          value={data.contact.serviceAreas.join(", ")}
          onChange={(v) => edit((d) => { d.contact.serviceAreas = v.split(",").map((s) => s.trim()).filter(Boolean); })}
          hint="Separate each city with a comma. These show as the city chips on the homepage, footer & contact page."
        />
      </Section>

      <Section title="Services" desc="Edit the name and description of each service group and the items inside it.">
        {data.services.map((s, gi) => (
          <div key={gi} className="border border-border rounded-sm p-4 bg-[#F8F9FA]">
            <Field label={`Group ${gi + 1} name`} value={s.group} onChange={(v) => edit((d) => { d.services[gi].group = v; })} />
            <div className="mt-3">
              <Field label="Group description" textarea rows={2} value={s.blurb} onChange={(v) => edit((d) => { d.services[gi].blurb = v; })} />
            </div>
            <div className="mt-3 space-y-3">
              {s.items.map((it, ii) => (
                <div key={ii} className="grid sm:grid-cols-2 gap-3 bg-white border border-border rounded-sm p-3">
                  <Field label="Service name" value={it.name} onChange={(v) => edit((d) => { d.services[gi].items[ii].name = v; })} />
                  <Field label="Description" value={it.desc} onChange={(v) => edit((d) => { d.services[gi].items[ii].desc = v; })} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Meet the Founders" desc="The Adrian & David bios. (Their photos stay the same.)">
        {data.founders.map((f, fi) => (
          <div key={fi} className="border border-border rounded-sm p-4 bg-[#F8F9FA] space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name" value={f.name} onChange={(v) => edit((d) => { d.founders[fi].name = v; })} />
              <Field label="Role / title" value={f.role} onChange={(v) => edit((d) => { d.founders[fi].role = v; })} />
            </div>
            <Field label="Quote" value={f.quote} onChange={(v) => edit((d) => { d.founders[fi].quote = v; })} />
            <Field label="Bio" textarea value={f.bio} onChange={(v) => edit((d) => { d.founders[fi].bio = v; })} />
          </div>
        ))}
      </Section>

      <div className="sticky bottom-4 flex justify-end gap-3">
        <button
          onClick={() => setData(clone(content))}
          data-testid="content-reset-btn"
          className="inline-flex items-center gap-2 bg-white border border-border text-charcoal px-5 py-3 rounded-sm font-600 text-sm hover:border-emerald transition-colors shadow-sm"
        >
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-btn"
          className="inline-flex items-center gap-2 bg-emerald text-white px-6 py-3 rounded-sm font-700 text-sm hover:bg-emerald-dark transition-colors disabled:opacity-60 shadow-md"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Changes</>}
        </button>
      </div>
    </div>
  );
}
